// Importing required modules from the 'three' library and custom components
import * as THREE from 'three';
import Camera from './components/Camera';
import EventDispatcher from './common/EventDispatcher';
import AssetLoader from './components/AssetLoader';
import GameScene from './components/GameScene';
import { Global } from './common/global';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import * as CANNON from 'cannon';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Server } from './common/callServer';

import { isMobile, isMobileOnly } from 'mobile-device-detect';
import RAPIER from '@dimforge/rapier3d-compat';

import gsap from 'gsap';
import Lights from './components/Lights';

import './styles/style.scss';
import './styles/Home.scss';
import './styles/Game.scss';
import './styles/Score.scss';
import './styles/Quiz.scss';

import Sounds from './components/Sounds';
import Control from './components/Control';
import Home from './UI/Home';
import Missionpick from './UI/Missionpick';
import Assignment from './UI/Assignment';
import Instruction from './UI/Instruction';
import UI from './components/UI';
import Result from './UI/Result';
import Quiz from './UI/Quiz';

let lastTime = 0;
let fps = 0;
let frameCount = 0;
let lastDeltaVec = new THREE.Vector3();

let world;
let debugLines; // THREE.LineSegments for debug
let debugOn = false;
let enableSleeping = true;

// Shared model registry
const models = new Map(); // key -> { gltf, baseMesh, instanced, instanceCount, bodies[] }
const rigidBodies = []; // keep for stepping
const colliders = []; // optional bookkeeping

export default class Game extends THREE.Scene {
  environmentMap = null;
  sizes = {
    width: isMobile ? window.innerWidth : window.innerHeight * 0.56,
    height: window.innerHeight,
  };

  constructor() {
    super();
    window.addEventListener('resize', this.onResize.bind(this));
    document
      .querySelector('#gyroButton')
      .addEventListener('click', this.updateGyroControl.bind(this));
  }
  init() {
    Global.isGyroActive = true;
    this.canRender = true;
    lastTime = 0;
    fps = 0;
    this.renderedLast = false;
    frameCount = 0;
    // this.stats = new Stats();
    // this.stats.showPanel(0);
    // document.body.appendChild(this.stats.dom);
    this.assetsLoaded = false;
    this.physicsEnabled = false;
    Global.gameTry = 1;
    Global.userRegistered = false;
    Global.controlType = null;
    this.fixedTimeStep = 1 / 60; // Fixed time step for physics (60 FPS)
    this.accumulator = 0; // Accumulated time since the last physics step

    this.physicsTimeScale = isMobile ? 0.95 : 1;
    this.gameTime = 0;
    this.events = EventDispatcher.getObj();
    this.events.addEventListener('LOADER_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAME_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents.bind(this));

    this.canvas = document.querySelector('canvas.webgl');
    this.clock = new THREE.Clock();

    this.camera = new Camera(
      50,
      this.sizes.width / this.sizes.height,
      0.1,
      100
    );
    this.camera.init();
    this.add(this.camera);

    // this.camera.position.set(0, 0.15, 1.6);

    // this.ui = new UI();
    // this.ui.init();

    this.gameSounds = new Sounds(this, this.camera);
    this.gameSounds.init();

    // this.gameControl = new GameControl(this);
    // this.gameControl.init();

    this.controls = new Control(this.camera, this.canvas);
    this.controls.init();

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      precision: 'highp',
      powerPreference: 'high-performance',
    });

    this.renderer.physicallyCorrectLights = true;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMappingExposure = 1;
    this.renderer.setClearColor(0xffffff, 1);
    this.renderer.domElement.style.background = 'transparent';
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.gammaFactor = 2.2;
    this.renderer.gammaOutput = false;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.setSize(this.sizes.width * 1, this.sizes.height * 1);
    this.renderer.setPixelRatio(
      Math.min(Math.max(2, window.devicePixelRatio), isMobile ? 1.5 : 1.5)
    );

    this.assetLoader = new AssetLoader(this);
    this.assetLoader.init();
    this.createLevel();
    this.onResize();
    this.tick();

    const rgbeLoader = new RGBELoader();
    rgbeLoader.load('./textures/environmentMaps/1/bg.hdr', (environmentMap) => {
      environmentMap.mapping = THREE.EquirectangularReflectionMapping;
      Global.envMap = environmentMap;
      this.background = environmentMap;
      this.assetLoader.prepare();
    });

    this.home = new Home();
    this.home.init();

    this.missionPick = new Missionpick();
    this.missionPick.init();

    this.assignment = new Assignment();
    this.assignment.init();

    this.instruction = new Instruction();
    this.instruction.init();

    this.ui = new UI();
    this.ui.init();

    this.result = new Result();
    this.result.init();

    this.quiz = new Quiz();
    this.quiz.init();
  }

  createLevel() {
    this.initPhysics();
  }
  createDebugLines() {
    const geo = new THREE.BufferGeometry();
    const mat = new THREE.LineBasicMaterial({
      transparent: true,
      opacity: 0.9,
    });
    debugLines = new THREE.LineSegments(geo, mat);
    debugLines.frustumCulled = false;
    scene.add(debugLines);
  }
  async initPhysics() {
    this.gameScene = new GameScene(
      this,
      this.renderer,
      this.world,
      this.camera
    );
    this.gameScene.init();

    // if (this.assetsLoaded) {
    //   this.setUpGame();
    // }
  }

  startGame() {
    Global.isGameActive = true;
  }

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'load_complete':
        if (this.assetsLoaded) return;

        this.assetsLoaded = true;
        document.querySelector('#splash_scr').classList.remove('active');
        document.querySelector('.PrivacyNoticeContainer').style.display =
          'block';
        this.setUpGame();
        break;
      case 'replay_game':
        this.replayGame(false);
        break;

      case 'init_scene':
        this.showScene();

        break;
      case 'start_game':
        this.startGame();
        this.startGameTimer();
        break;
      case 'game_over':
        this.stopGame(true);

        break;
      // case 'goal':
      case 'defend':
      case 'retry':
        this.prepareNextTry(data['message']['event_type']);
        break;
    }
  }
  showScene() {
    this.canRender = true;
    // this.events.dispatchEvent({
    //   type: 'SOUND_EVENTS',
    //   message: {
    //     event_type: 'stop_sound',
    //     data: {
    //       sndKey: 'music',
    //     },
    //   },
    // });
    // gsap.delayedCall(0.1, () => {
    //   this.events.dispatchEvent({
    //     type: 'SOUND_EVENTS',
    //     message: {
    //       event_type: 'play_sound',
    //       data: {
    //         sndKey: 'crowd',
    //       },
    //     },
    //   });
    // });
    // gsap.delayedCall(0.25, () => {
    //   document.querySelector('#game_view').classList.add('active');
    // });
  }
  async onGameFinish() {}

  startGameTimer() {
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'start_time',
        data: {},
      },
    });
    // document.querySelector('.game_header').classList.add('active');
  }
  // stopTimer() {
  //   this.gameTimer && clearInterval(this.gameTimer);
  // }
  // updateTime() {
  //   this.gameTime += 100;
  //   let totalSeconds = Math.floor(this.gameTime / 1000); // Convert to total seconds
  //   let minutes = Math.floor(totalSeconds / 60); // Calculate total minutes
  //   let seconds = totalSeconds % 60; // Calculate remaining seconds

  //   document.querySelector('#info_time .txt').innerHTML = `${
  //     minutes < 10 ? '0' : ''
  //   }${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  // }

  setUpGame() {
    this.environment = Global.envMap;
    // this.background = Global.envMap;
    this.backgroundIntensity = 1;

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'create_scene',
        data: {},
      },
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'add_control',
        data: {},
      },
    });

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'show_screen',
        data: {
          screenKey: 'Home',
        },
      },
    });

    //Start Quiz

    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: {
    //     event_type: 'show_screen',
    //     data: {
    //       screenKey: 'QuizHome',
    //     },
    //   },
    // });

    // this.events.dispatchEvent({
    //   type: 'GAME_EVENTS',
    //   message: {
    //     event_type: 'show_volume',
    //     data: {},
    //   },
    // });

    // this.events.dispatchEvent({
    //   type: 'GAME_EVENTS',
    //   message: {
    //     event_type: 'start_game',
    //     data: {},
    //   },
    // });
  }

  stopGame(quickTransition) {
    Global.isGameActive = false;
    this.canRender = false;
    this.hideHeader();
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'hide_intro',
        data: {},
      },
    });

    this.events.dispatchEvent({
      type: 'GAME_EVENTS',
      message: {
        event_type: 'dispose',
        data: {},
      },
    });
    this.dispose();

    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'stop_sound',
        data: {
          sndKey: 'crowd',
        },
      },
    });
    gsap.delayedCall(0.1, () => {
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: {
          event_type: 'play_sound',
          data: {
            sndKey: 'music',
          },
        },
      });
    });
    Global.serverObj.send(Global.URL_DATA, null, null, {
      saveType: 'score',
      score: Global.totalGoalsSaved,
      yob: Global.yob,
      gender: Global.gender,
      name: Global.userName,
      mobile: Global.mobile,
      email: Global.email,
      jerseyname: Global.avatarName,
      location: Global.location,
      IGhandle: Global.IGHandle,
      uniqID: Global.U_ID,
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'show_screen',
        data: {
          screenKey: 'Score',
        },
      },
    });
  }
  prepareNextTry(type) {
    // gsap.delayedCall(type == 'retry' ? 0 : 4, this.setupFreekick.bind(this));
  }
  // showHeader() {
  //   document.querySelector('#game_header').classList.add('active');

  //   gsap.delayedCall(0.1, () => {
  //     document.querySelector('#game_header').classList.add('active2');
  //   });
  // }
  // hideHeader() {
  //   document.querySelector('#game_header').classList.remove('active');
  //   document.querySelector('#game_header').classList.remove('active2');
  // }

  // Method to handle game replay
  replayGame(isGameTry = false) {
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'start_game',
        data: {},
      },
    });
    return;
    Global.gameTry++;

    this.createLevel();

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'create_scene',
        data: {},
      },
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'init_scene',
        data: {},
      },
    });

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'update_post_feature',
        data: {},
      },
    });
    gsap.delayedCall(1, () => {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'start_game',
          data: {},
        },
      });
      /*  this.events.dispatchEvent({
        type: 'CAMERA_EVENTS',
        message: {
          event_type: 'add_intro_view',
          data: {},
        },
      }); */
    });

    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: {
    //     event_type: 'start_game',
    //     data: {},
    //   },
    // });
    // this.onStart();
  }
  // Event handler for window resize
  onResize() {
    // Update sizes
    this.sizes.width = isMobile ? window.innerWidth : window.innerHeight * 0.56;
    this.sizes.height = window.innerHeight;

    // Dispatching events to update camera and renderer sizes
    this.events.dispatchEvent({
      type: 'CAMERA_EVENTS',
      message: {
        event_type: 'resize',
        data: {
          aspect: this.sizes.width / this.sizes.height,
          sizes: this.sizes,
        },
      },
    });

    // Updating renderer size and pixel ratio
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(
      Math.min(Math.max(2, window.devicePixelRatio), isMobile ? 2 : 1.5)
    );
  }

  // Update method to render the scene and dispatch update events
  update(now, delta) {
    // if (!Global.isGameActive) return;
    // if (!this.renderedLast) {
    //   this.renderer.render(this, this.camera);
    // }
    this.renderer.render(this, this.camera);
    this.renderedLast = !this.renderedLast;

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'update',
        data: {
          delta: delta,
          camera: this.camera,
          now: now,
        },
      },
    });
  }

  tick(now) {
    if (!this.canRender) {
      window.requestAnimationFrame(this.tick.bind(this));
      return;
    }
    this.stats && this.stats.begin();
    // this.controls && this.controls.update();

    const delta = (now - lastTime) / 1000; // Convert milliseconds to seconds
    lastTime = now;

    frameCount++;
    if (delta) {
      fps = frameCount / delta;
      frameCount = 0;
    } else {
    }

    let ticks = Math.ceil(delta / (1 / 120));
    ticks = Math.min(ticks, 20);
    Global.fps = new THREE.Vector3(0, Global.fps, 0).lerp(
      new THREE.Vector3(0, fps, 0),
      0.1
    ).y;
    const newDelta = Math.min(
      (delta ? Math.min(delta * 100, 1) : 1) * (120 / (fps || 1)),
      10
    );
    Global.delta = THREE.MathUtils.lerp(Global.delta, newDelta, 0.01);
    const scaledDelta = delta * this.physicsTimeScale;
    this.update(now, lastDeltaVec.y);

    // if (this.world) {
    //   const p = this.world.integrationParameters;
    //   if (p) {
    //     // if ('numSolverIterations' in p) p.numSolverIterations = 0.1;
    //     if ('numAdditionalFrictionIterations' in p)
    //       p.numAdditionalFrictionIterations = 0.1;
    //   }
    // }
    if (this.world) {
      const delta2 = this.clock.getDelta();
      this.world.timestep = Math.min(delta2, 0.01) * 2;
      this.world.step();
      // this.world.step();
    }

    // if (!isNaN(scaledDelta)) {
    //   this.accumulator += scaledDelta;
    //   while (this.accumulator >= this.fixedTimeStep) {
    //     // this.ball && this.ball.updateTrail();
    //     this.world && this.world.step && this.world.step(); // Step the physics simulation

    //     this.accumulator -= this.fixedTimeStep; // Reduce the accumulator
    //   }
    // }

    this.stats && this.stats.end();
    window.requestAnimationFrame(this.tick.bind(this));
  }
  dispose() {
    document.querySelector('#game_view').classList.remove('active');
    document.querySelector('#game_view').classList.remove('active2');

    if (this.world) {
      while (this.world.bodies.length > 0) {
        this.world.removeBody(this.world.bodies[0]);
      }
      // Optionally remove constraints if used
      // while (this.world.constraints.length > 0) {
      //   this.world.removeConstraint(this.world.constraints[0]);
      // }
      this.world = null;
    }

    this.ballFloorContactMaterial = null;
    this.ballNetContactMaterial = null;

    // Clear references to materials
    Global.ballMaterial = null;
    Global.floorMaterial = null;
    Global.netMaterial = null;
    Global.netMaterial2 = null;
  }

  updateGyroControl() {
    Global.isGyroActive = !Global.isGyroActive;

    if (Global.isGyroActive) {
      document.querySelector('#gyroButton img').src = './ui/Gyro-ON.png';
    } else {
      document.querySelector('#gyroButton img').src = './ui/Gyro-OFF.png';
    }
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: Global.isGyroActive ? 'disable_control' : 'enable_control',
        data: {},
      },
    });
  }
}

window.onload = async function () {
  checkDevice();
};

function setUID(v) {
  Global.U_ID = JSON.parse(v)['UID'];
  Global.gameKey = JSON.parse(v)['gamekey'];

  Global.serverObj.setDetails(Global.U_ID, Global.gameKey);
}

function checkDevice() {
  //

  if (!isMobile) {
    document.querySelector('.PrivacyNoticeContainer').style.display = 'block';

    document.querySelector('#desktop_scr').classList.add('active');
    document.querySelector('#desktop_scr .logo').classList.add(`active`);
    document.querySelector('#desktop_scr .left .desc').classList.add(`active`);
    document.querySelector('#desktop_scr .right .desc').classList.add(`active`);
    // let titleIndex = -10;
    // let titleInt = setInterval(() => {
    //   if (titleIndex < 18) {
    //     document
    //       .querySelector('#desktop_scr .title')
    //       .classList.remove(`title${titleIndex}`);
    //   }

    //   titleIndex++;
    //   if (titleIndex > 18) {
    //     document.querySelector('#desktop_scr .logo').classList.add(`active`);
    //     document
    //       .querySelector('#desktop_scr .left .desc')
    //       .classList.add(`active`);
    //     document
    //       .querySelector('#desktop_scr .right .desc')
    //       .classList.add(`active`);

    //     clearInterval(titleInt);
    //   } else {
    //     document
    //       .querySelector('#desktop_scr .title')
    //       .classList.add(`title${titleIndex}`);
    //   }
    // }, 30);
    document.querySelector('#splash_scr').classList.add('desktop');
    document.querySelector('#homeScr').classList.add('desktop');
    document.querySelector('#taskInfo').classList.add('desktop');
    document.querySelector('#instruction').classList.add('desktop');
    document.querySelector('#game_header').classList.add('desktop');
    document.querySelector('#item_tray').classList.add('desktop');
    document.querySelector('#scoreScr').classList.add('desktop');
    document.querySelector('#game_view').classList.add('desktop');
  } else {
    // document.querySelector('.PrivacyNoticeContainer').style.display = 'block';
    document.querySelector('#splash_scr').classList.add('active');

    document.querySelector('#splash_scr').classList.add('active2');

    const params = new URLSearchParams(window.location.search);

    const utm_source = params.get('utm_source');
    Global.utm_source = utm_source ? utm_source : 'direct';

    const at_ptr_name = params.get('at_ptr_name');
    Global.at_ptr_name = at_ptr_name ? at_ptr_name : null;

    const utm_campaign = params.get('utm_campaign');
    Global.utm_campaign = utm_campaign ? utm_campaign : null;

    const at_format = params.get('at_format');
    Global.at_format = at_format ? at_format : null;

    Global.serverObj = new Server();
    Global.serverObj.send(
      Global.URL_CREATE,
      setUID.bind(this),
      null,
      {
        device: isMobile ? 'mobile' : 'web',
        fresh: true,
        utm_source: Global.utm_source,
        at_ptr_name: Global.at_ptr_name,
        utm_campaign: Global.utm_campaign,
        at_format: Global.at_format,
      },
      'POST',
      null,
      false
    );

    const game = new Game();
    game.init();
  }
}
